import _ from "lodash";

/**
 * The confirmation payload arrives in two shapes and the blocks shouldn't have
 * to care which. A one-time purchase hangs everything off `order`; a
 * subscription hangs it off `subscription.last_dispatch`, where the line items
 * live on the *dispatch* (and call their pre-discount figure `price`) while the
 * money lives on the dispatch's `order` (which calls it `subtotal`).
 *
 * Normalising here means each block reads one flat object, and the
 * subscription/one-time branching exists in exactly one place instead of being
 * repeated per field the way `OrderBreakdownBlock` used to.
 */

const BRAND_LABELS = {
  visa: "Visa",
  mastercard: "Mastercard",
  amex: "American Express",
  "american express": "American Express",
};

const capitalise = (value) =>
  value ? value.charAt(0).toUpperCase() + value.slice(1) : "";

const isZero = (money) =>
  !money || money.amount === "0.00" || Number(money.amount) === 0;

/**
 * "Every 4 weeks", "Every week". Matches the phrasing `RecurringCost` and
 * `BasketItem` already use elsewhere.
 */
const frequencyLabel = (subscription) => {
  const interval = subscription?.interval;
  const period = subscription?.period;

  if (!interval || !period) {
    return null;
  }

  return `Every ${interval === 1 ? period : `${interval} ${period}s`}`;
};

/**
 * Freebies the API models as children of a paid line rather than lines of their
 * own — the free Grind Tin is the one that matters. `removed` children are
 * items the customer opted out of, so they're dropped.
 */
const includedChildren = (lineItem, parentKey) =>
  (lineItem.children || [])
    .filter((child) => child.type === "rule" && child.removed === false)
    .map((child, idx) => ({
      id: child.id ?? `${parentKey}-child-${idx}`,
      product: child.product,
      variant: child.variant,
      quantity: child.quantity,
      // Dispatch children carry `price`, order children carry `subtotal`.
      subtotal: child.price ?? child.subtotal,
      discount: child.discount,
      total: child.total,
      subtitle: null,
    }));

const normaliseLineItem = (lineItem, key, subtitle) => ({
  id: lineItem.id ?? key,
  product: lineItem.product,
  variant: lineItem.variant,
  quantity: lineItem.quantity,
  subtotal: lineItem.price ?? lineItem.subtotal,
  discount: lineItem.discount,
  total: lineItem.total,
  subtitle,
});

const subscriptionLineItems = (subscription) => {
  const dispatch = subscription.last_dispatch || subscription;
  const label = frequencyLabel(subscription);

  return (dispatch.products || [])
    .filter((lineItem) => lineItem.removed !== true)
    .flatMap((lineItem, idx) => {
      const key = `item-${idx}`;

      return [
        normaliseLineItem(
          lineItem,
          key,
          lineItem.type === "subscription" ? label : null,
        ),
        ...includedChildren(lineItem, key),
      ];
    });
};

const orderLineItems = (order) =>
  (order.products || []).flatMap((lineItem, idx) => {
    const key = `item-${idx}`;

    return [
      normaliseLineItem(lineItem, key, null),
      ...includedChildren(lineItem, key),
    ];
  });

const getConfirmationOrder = (completedCheckout) => {
  const subscription = completedCheckout?.subscription;
  const isSubscription = Boolean(
    completedCheckout?.type === "subscription" && subscription,
  );

  const order = isSubscription
    ? _.get(subscription, "last_dispatch.order")
    : completedCheckout?.order;

  const payment = order?.payment;
  const paymentMethod = payment?.payment_method || subscription?.payment_method;
  const card = paymentMethod?.card || order?.payment_card || null;

  // `payment_method.address` is postcode-only, so it is deliberately not a
  // fallback here — a billing address rendered as a lone postcode reads as a
  // bug. Requiring `address1` also keeps partially-populated objects out.
  const billing = order?.billing_address ?? subscription?.billing_address;

  return {
    orderNumber: order?.order_number ?? null,

    lineItems: isSubscription
      ? subscriptionLineItems(subscription)
      : orderLineItems(completedCheckout?.order || {}),

    shippingMethod: order?.shipping_method ?? null,

    // Only a discount that actually moved the price is worth a row.
    discount: isZero(order?.discount) ? null : order.discount,

    total: order?.total ?? payment?.captured ?? null,
    amountPaid: payment?.captured ?? order?.total ?? null,

    card: card
      ? {
          brand: card.brand,
          // Never collected today; rendered only if the API starts sending it.
          name: paymentMethod?.name ?? null,
          last4: card.last4,
          label: [capitalise(card.funding), BRAND_LABELS[String(card.brand).toLowerCase()] ?? capitalise(card.brand)]
            .filter(Boolean)
            .join(" "),
        }
      : null,

    billingAddress: billing?.address1 ? billing : null,
  };
};

export { getConfirmationOrder, frequencyLabel };
